import { useCallback, useEffect, useState } from "react";
import { NarAgentGridService } from "../../../../../api/services/narAgentGrid/narAgentGridService";
import { ListPayerJCodesResponse } from "../../../../../api/services/narAgentGrid/types";

/** Inputs for {@link useListJCodesByPayer}. */
export interface UseListJCodesByPayerParams {
  /** Payer name used to list distinct auth-grid J-codes. */
  payerName: string;
}

/** State returned by {@link useListJCodesByPayer}. */
export interface UseListJCodesByPayerReturn {
  /** Distinct J-code rows returned by the API, or an empty list before fetch. */
  data: ListPayerJCodesResponse;
  /** True while the list request is in flight. */
  loading: boolean;
  /** Error message when the fetch fails, otherwise null. */
  error: string | null;
  /** Re-runs the list request for the current payer name. */
  refetch: () => Promise<ListPayerJCodesResponse | undefined>;
}

/**
 * Loads distinct auth-grid J-codes for a payer whenever `payerName` is set.
 * In-flight requests are aborted when the payer changes or the hook unmounts.
 */
export const useListJCodesByPayer = ({
  payerName,
}: UseListJCodesByPayerParams): UseListJCodesByPayerReturn => {
  const [data, setData] = useState<ListPayerJCodesResponse>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchJCodes = useCallback(
    async (
      signal?: AbortSignal,
    ): Promise<ListPayerJCodesResponse | undefined> => {
      const trimmedPayerName = payerName.trim();
      if (!trimmedPayerName) {
        setData([]);
        setError(null);
        setLoading(false);
        return [];
      }

      setLoading(true);
      setError(null);

      try {
        const response = await NarAgentGridService.listJCodesByPayer(
          { payer_name: trimmedPayerName },
          signal,
        );
        setData(response);
        setLoading(false);
        return response;
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return undefined;
        }

        const message = err instanceof Error ? err.message : String(err);
        setData([]);
        setError(message);
        setLoading(false);
        return undefined;
      }
    },
    [payerName],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchJCodes(controller.signal);

    return () => {
      controller.abort();
    };
  }, [fetchJCodes]);

  const refetch = useCallback(async () => {
    return fetchJCodes();
  }, [fetchJCodes]);

  return {
    data,
    loading,
    error,
    refetch,
  };
};

export default useListJCodesByPayer;
